/**
 * Resolve which email provider (Gmail or Outlook) is connected for sending.
 * OAuth tokens are resolved server-side from (tenant_id, user_id); do not fetch or forward tokens in FE.
 */

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

async function fetchProviderStatus(provider, tenantId, userId) {
  const endpoint = provider === 'outlook' ? 'outlook' : 'gmail';
  const response = await fetch(
    `${API_BASE_URL}/api/${endpoint}/status?tenant_id=${tenantId}&user_id=${userId}`,
    { credentials: 'include' },
  );
  if (!response.ok) return false;
  const data = await response.json().catch(() => ({}));
  return data.connected === true;
}

/**
 * @param {{ tenantId: string, userId: string, preferredOutlookAlias?: string|null, preferredGmailAlias?: string|null }} opts
 * @returns {Promise<{ provider: 'gmail'|'outlook' }>}
 */
export async function getConnectedEmailProviderTokens({
  tenantId,
  userId,
  preferredOutlookAlias,
  preferredGmailAlias,
}) {
  if (!tenantId || !userId) {
    throw new Error('No email provider connected. Please connect Gmail or Outlook in Settings.');
  }

  let gmailConnected = false;
  let outlookConnected = false;
  try {
    [gmailConnected, outlookConnected] = await Promise.all([
      fetchProviderStatus('gmail', tenantId, userId),
      fetchProviderStatus('outlook', tenantId, userId),
    ]);
  } catch {
    // Status unavailable — fall through to error below.
  }

  const preferredBothOrder = preferredOutlookAlias
    ? ['outlook', 'gmail']
    : preferredGmailAlias
      ? ['gmail', 'outlook']
      : ['outlook', 'gmail'];

  if (outlookConnected || gmailConnected) {
    if (outlookConnected && gmailConnected) {
      const provider = preferredBothOrder.find((p) =>
        p === 'outlook' ? outlookConnected : gmailConnected,
      );
      if (provider) return { provider };
    } else if (outlookConnected) {
      return { provider: 'outlook' };
    } else if (gmailConnected) {
      return { provider: 'gmail' };
    }
  }

  throw new Error('No email provider connected. Please connect Gmail or Outlook in Settings.');
}
